import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Typography, Divider, TextField, IconButton, Tooltip, Box, Modal, Radio, RadioGroup, FormControlLabel, FormControl, Checkbox, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate } from "react-router-dom";
import {
  CartContainerWrapper,
  ClearCartWrapper,
  CartItemsWrapper,
  CartSummaryWrapper,
  CartSummaryContent,
  SummaryLeftSection,
  SummaryRightSection,
  SummaryRightInner,
  SummaryRightTop,
  SummaryRightSpacer,
  SummaryRightBottom,
  ProductCard,
  ProductCardContent,
  ProductCardActions,
  ProductImage,
  ToSectionWrapper,
  ToSectionHeader,
  ToSectionContent,
  CartActionsWrapper,
} from "./CartContainer.styles";
import {
  getCartUnified,
  updateCartQty,
  removeCartLine,
  clearUnifiedCart,
  isLoggedIn,
} from "../../cart/cartBridge";
import { createOrderFromCart, getProfile, apiRequest, updateAddress } from "../../api/api";
import {useToast} from '../../components/ToastProvider/ToastProvider';

const numberFmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const ZERO_TOTALS = { subtotal: 0, shipping: 0, discount: 0, tax: 0, total: 0 };

function recomputeFromItems(items, productDetails = {}) {
  const safe = Array.isArray(items) ? items : [];
  // Exclude ON_DEMAND items from subtotal calculation
  const subtotal = safe.reduce(
    (a, i) => {
      // Check if item is ON_DEMAND (from product details or item itself)
      const product = productDetails[i.product_id];
      const productPricingMode = product?.pricing_mode;
      const unitPriceNum = Number(i.unit_price || 0);
      // Check if ON_DEMAND: either pricing_mode is ON_DEMAND, or price is 0 and not explicitly FIXED
      const isOnDemand = productPricingMode === 'ON_DEMAND' || 
                         (unitPriceNum === 0 && productPricingMode !== 'FIXED');
      
      if (isOnDemand) {
        return a; // Exclude from subtotal
      }
      
      return a +
        Math.max(
          0,
          Number(i.unit_price || 0) * Number(i.quantity || 0) -
            Number(i.discount_amount || 0)
        );
    },
    0
  );
  const item_count = safe.reduce((a, i) => a + Number(i.quantity || 0), 0);
  return {
    item_count,
    subtotal,
    discount_total: 0,
    tax_total: 0,
    grand_total: subtotal,
  };
}

function formatAddressLine(addr) {
  if (!addr) return "—";
  const parts = [addr.street_address, addr.suburb_locality].filter(Boolean);
  if (!parts.length) return "—";
  return `${parts.join(" ")}...`;
}

function formatFullAddress(addr) {
  if (!addr) return "—";
  const parts = [
    addr.street_address,
    addr.suburb_locality,
    addr.city_town || addr.city,
    addr.state_province_region || addr.state_province,
    addr.postal_zip_code || addr.postal_code,
    addr.country
  ].filter(Boolean);
  if (!parts.length) return "—";
  return parts.join(", ");
}

function getStockQty(item) {
  const candidates = [
    item?.stock_quantity,
    item?.available_quantity,
    item?.available,
    item?.product?.stock_quantity,
    item?.product?.available_quantity,
  ];
  const firstNum = candidates.find((v) => Number.isFinite(Number(v)));
  const n = Number(firstNum);
  return Number.isFinite(n) ? n : Infinity;
}

export default function CartContainer({ onClose, scrolled }) {
  const navigate = useNavigate();
  const { showToast } = useToast(); // <-- hook
  const [cart, setCart] = useState(null);
  const [auth, setAuth] = useState(isLoggedIn());
  const [creating, setCreating] = useState(false);
  const [productDetails, setProductDetails] = useState({});
  const inFlightRef = useRef(false);

  // Function to get product image
  const getProductImage = (item) => {
    // First try to get from product details
    const product = productDetails[item.product_id];
    if (product && product.image_urls && product.image_urls.length > 0) {
      return product.image_urls[0];
    }
    // Fallback to placeholder
    return '/ProductHolder.png';
  };

  // Fetch product details for cart items
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!cart || !cart.items || cart.items.length === 0) {
        setIsLoadingProducts(false);
        return;
      }
      
      const productIds = cart.items.map(item => item.product_id).filter(Boolean);
      if (productIds.length === 0) {
        setIsLoadingProducts(false);
        return;
      }
      
      setIsLoadingProducts(true);
      try {
        const response = await apiRequest(`/products/?ids=${productIds.join(',')}`);
        if (response && Array.isArray(response)) {
          const details = {};
          response.forEach(product => {
            details[product.id] = product;
          });
          setProductDetails(details);
        }
      } catch (error) {
        console.error('Failed to fetch product details:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProductDetails();
  }, [cart]);
  const [panelName, setPanelName] = useState("—");
  const [panelAddress, setPanelAddress] = useState("—");
  const [panelMobile, setPanelMobile] = useState("—");
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null); // Store selected address object
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [shippingData, setShippingData] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [allShippingAddresses, setAllShippingAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [shippingPriority, setShippingPriority] = useState('normal'); // 'normal' or 'express'
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingCart, setIsLoadingCart] = useState(true); // For initial cart load

  const load = useCallback(async () => {
    setIsLoadingCart(true);
    const data = await getCartUnified();
    if (isLoggedIn() && (!data?.items || data.items.length === 0)) {
      setCart({
        ...data,
        item_count: 0,
        subtotal: 0,
        discount_total: 0,
        tax_total: 0,
        shipping_cost: 0,
        grand_total: 0,
      });
    } else {
      // Ensure stable ordering of cart items
      if (data?.items) {
        data.items = data.items.sort((a, b) => {
          // Sort by ID if available, otherwise by product_id
          const aId = a.id || a.product_id;
          const bId = b.id || b.product_id;
          return aId - bId;
        });
      }
      setCart(data);
    }
    setIsLoadingCart(false);
  }, []);

  const loadPanel = useCallback(async () => {
    if (!isLoggedIn()) {
      setPanelName("—");
      setPanelAddress("—");
      setPanelMobile("—");
      setSelectedShippingAddress(null);
      return;
    }
    try {
      const prof = await getProfile();
      const p = prof?.profile || prof;
      const addresses = Array.isArray(p?.addresses) ? p.addresses : [];
      
      // Filter only shipping addresses
      const shippingAddresses = addresses.filter((a) => a.is_shipping);
      setAllShippingAddresses(shippingAddresses);
      
      // Find default shipping address or first shipping address
      let ship =
        shippingAddresses.find((a) => a.is_default) ||
        shippingAddresses[0];

      setSelectedShippingAddress(ship);
      setSelectedAddressId(ship?.id || null);
      
      setPanelName(ship?.recipient_name || p?.full_name || "—");
      setPanelMobile(ship?.phone_number || p?.mobile_no || "—");
      setPanelAddress(formatAddressLine(ship));
    } catch (e) {
      console.error("Failed to load profile for shipping panel", e);
      setPanelName("—");
      setPanelAddress("—");
      setPanelMobile("—");
      setSelectedShippingAddress(null);
    }
  }, []);
  
  const handleOpenAddressModal = useCallback(async () => {
    if (!auth) return;
    
    try {
      const prof = await getProfile();
      const p = prof?.profile || prof;
      const addresses = Array.isArray(p?.addresses) ? p.addresses : [];
      const shippingAddresses = addresses.filter((a) => a.is_shipping);
      setAllShippingAddresses(shippingAddresses);
      
      // Set current selected address ID
      if (selectedShippingAddress?.id) {
        setSelectedAddressId(selectedShippingAddress.id);
      } else if (shippingAddresses.length > 0) {
        const defaultAddr = shippingAddresses.find((a) => a.is_default) || shippingAddresses[0];
        setSelectedAddressId(defaultAddr?.id || null);
      }
      
      setAddressModalOpen(true);
    } catch (e) {
      console.error("Failed to load addresses for modal", e);
      showToast("Failed to load shipping addresses", "error");
    }
  }, [auth, selectedShippingAddress, showToast]);
  
  const handleCloseAddressModal = () => {
    setAddressModalOpen(false);
  };
  
  const handleSaveAddress = () => {
    if (!selectedAddressId) {
      showToast("Please select an address", "warning");
      return;
    }
    
    const selectedAddr = allShippingAddresses.find((a) => a.id === selectedAddressId);
    if (!selectedAddr) {
      showToast("Selected address not found", "error");
      return;
    }
    
    setSelectedShippingAddress(selectedAddr);
    setPanelName(selectedAddr?.recipient_name || "—");
    setPanelMobile(selectedAddr?.phone_number || "—");
    setPanelAddress(formatAddressLine(selectedAddr));
    setAddressModalOpen(false);
    showToast("Shipping address updated", "success");
    
    // Recalculate shipping with new address
    setTimeout(() => {
      calculateShipping();
    }, 100);
  };

  const items = useMemo(() => cart?.items || [], [cart?.items]);

  // Check if any item is ON_DEMAND
  const hasOnDemandItems = useMemo(() => {
    return items.some(item => {
      const product = productDetails[item.product_id];
      const productPricingMode = product?.pricing_mode;
      const unitPriceNum = Number(item.unit_price || 0);
      // Check if ON_DEMAND: either pricing_mode is ON_DEMAND, or price is 0 and not explicitly FIXED
      return productPricingMode === 'ON_DEMAND' || 
             (unitPriceNum === 0 && productPricingMode !== 'FIXED');
    });
  }, [items, productDetails]);

  // Check if cart has lenovo-top-choice products (for express shipping checkbox)
  const hasLenovoTopChoice = useMemo(() => {
    return items.some(item => {
      const product = productDetails[item.product_id];
      const vendor = product?.vendor || item.product_vendor || '';
      return vendor && vendor.toLowerCase() === 'lenovo-top-choice';
    });
  }, [items, productDetails]);

  const calculateShipping = useCallback(async (expressPriorityOverride = null) => {
    if (!auth || !cart?.items?.length) {
      setShippingData(null);
      setIsCalculatingShipping(false);
      return;
    }

    setIsCalculatingShipping(true);
    try {
      // Use override if provided, otherwise use current shippingPriority state
      const expressPriority = expressPriorityOverride !== null 
        ? expressPriorityOverride 
        : (hasLenovoTopChoice && shippingPriority === 'express');
      
      const response = await apiRequest(
        `/cart/shipping?express_priority=${expressPriority}`, 
        "GET"
      );
      setShippingData(response);
      
      // Update cart with calculated tax and shipping from shipping API
      if (response.tax_total !== undefined) {
        setCart(prev => prev ? { 
          ...prev, 
          tax_total: response.tax_total,
          shipping_cost: response.total_shipping_cost || prev.shipping_cost
        } : prev);
      }
    } catch (error) {
      console.error("Failed to calculate shipping:", error);
      setShippingData(null);
    } finally {
      setIsCalculatingShipping(false);
    }
  }, [cart?.items, auth, hasLenovoTopChoice, shippingPriority]);

  useEffect(() => {
    load();
    loadPanel();
    const onUpd = () => { load(); };
    const onStorage = (e) => {
      if (e.key === "accessToken") {
        setAuth(isLoggedIn());
        load();
        loadPanel();
      }
    };
    window.addEventListener("cart:updated", onUpd);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("cart:updated", onUpd);
      window.removeEventListener("storage", onStorage);
    };
  }, [load, loadPanel]);

  useEffect(() => {
    calculateShipping();
  }, [calculateShipping]);

  // Reset shipping priority to normal if no Lenovo Top Choice products
  useEffect(() => {
    if (!hasLenovoTopChoice && shippingPriority === 'express') {
      setShippingPriority('normal');
    }
  }, [hasLenovoTopChoice, shippingPriority]);

  const totals = useMemo(() => {
    if (!cart) return ZERO_TOTALS;
    if (auth && items.length === 0) return ZERO_TOTALS;
    
    // Recalculate subtotal excluding ON_DEMAND items
    const recalculatedSubtotal = recomputeFromItems(items, productDetails).subtotal;
    const subtotal = hasOnDemandItems ? 0 : Number(cart.subtotal || recalculatedSubtotal);
    
    // Shipping: Check vendor-specific logic
    // Backend now returns null for "To be calculated" or a number with margin already applied
    let shipping = null;
    if (hasOnDemandItems) {
      shipping = null; // To be calculated (ON_DEMAND items)
    } else if (shippingData) {
      // Backend returns null if any vendor needs calculation, or a number with margin applied
      if (shippingData.total_shipping_cost === null || shippingData.total_shipping_cost === undefined) {
        shipping = null; // To be calculated
      } else if (shippingData.has_manual_quotes) {
        shipping = null; // To be calculated (ANY product needs manual quote)
      } else {
        shipping = Number(shippingData.total_shipping_cost || 0);
      }
    } else {
      shipping = Number(cart.shipping_cost ?? 0);
    }
    
    // Note: Express shipping $215 is now handled by backend:
    // - LCR: Always added automatically
    // - Lenovo Top Choice: Added if express_priority=true (passed in API call)
    // Frontend no longer needs to add $215 manually
    
    // Tax: For Mauritius only (15% of subtotal + shipping), backend handles country check
    // Backend returns 0 for non-Mauritius countries, so we just use that value
    // For Mauritius, if ON_DEMAND or shipping is null, tax should be "To be calculated"
    let tax = Number(cart.tax_total ?? 0);
    let taxNeedsCalculation = false;
    
    // If there's any tax value from backend AND (ON_DEMAND items OR shipping pending), tax needs recalculation
    if ((hasOnDemandItems || shipping === null) && tax > 0) {
      tax = null; // To be calculated (Mauritius only)
      taxNeedsCalculation = true;
    }
    
    const discount = hasOnDemandItems ? 0 : Number(cart.discount_total || 0);

    // Total: null if any component is null (hasOnDemandItems, shipping, or tax for Mauritius)
    const total =
      hasOnDemandItems || shipping === null || taxNeedsCalculation
        ? null
        : subtotal + shipping + tax - discount;
    
    // Determine if shipping disclaimer should be shown (shipping is calculated, not "To be calculated")
    const showShippingDisclaimer = shipping !== null && shipping > 0;
    
    // Determine if total disclaimer should be shown (total is calculated, not "To be calculated")
    const showTotalDisclaimer = total !== null && total > 0;
    
    return {
      subtotal,
      shipping,
      discount,
      tax,
      total,
      hasOnDemandItems,
      showShippingDisclaimer,
      showTotalDisclaimer,
    };
  }, [cart, auth, items, productDetails, shippingData, hasOnDemandItems]);

  const handleQty = async (it, raw) => {
    const stock = getStockQty(it);
    const next = Math.max(1, Math.min(stock, Number(String(raw).replace(/\D/g, "")) || 1));
    await updateCartQty(it, next);
    setCart((prev) => {
      if (!prev) return prev;
      const clone = structuredClone(prev);
      const idx = clone.items.findIndex(
        (x) => x.id === it.id || x.product_id === it.product_id
      );
      if (idx > -1) {
        clone.items[idx].quantity = next;
        clone.items[idx].line_total =
          Number(clone.items[idx].unit_price) * next -
          Number(clone.items[idx].discount_amount || 0);
      }
      const t = recomputeFromItems(clone.items, productDetails);
      // Preserve the existing tax_total from backend calculation and maintain original order
      return { ...clone, ...t, tax_total: prev.tax_total };
    });
    
    // Trigger shipping/tax recalculation after quantity change
    setTimeout(() => {
      calculateShipping();
    }, 100);
  };

  const changeQtyBy = async (it, delta) => {
    const current = Number(it.quantity || 1);
    const stock = getStockQty(it);
    const next = Math.max(1, Math.min(stock, current + delta));
    if (next === current) return;
    await handleQty(it, String(next));
  };

  const handleRemove = async (it) => {
    await removeCartLine(it);
    setCart((prev) => {
      if (!prev) return prev;
      const newItems = (prev.items || []).filter(
        (x) => (x.id ?? x.product_id) !== (it.id ?? it.product_id)
      );
      const t = recomputeFromItems(newItems);
      // Preserve the existing tax_total from backend calculation and maintain original order
      return { ...prev, items: newItems, ...t, tax_total: prev.tax_total };
    });
    load();
    window.dispatchEvent(new CustomEvent("cart:updated", { detail: { action: "remove" } }));
    showToast("Item removed from cart.", "info"); // <-- toast
    
    // Trigger shipping/tax recalculation after item removal
    setTimeout(() => {
      calculateShipping();
    }, 100);
  };

  const handleClear = async () => {
    await clearUnifiedCart();
    setCart((prev) => {
      if (!prev) return prev;
      const t = recomputeFromItems([]);
      return { ...prev, items: [], ...t };
    });
    load();
    window.dispatchEvent(new CustomEvent("cart:updated", { detail: { action: "clear" } }));
    showToast("Cart cleared.", "info"); // <-- toast
  };

  const handleCreatePreorder = async () => {
    if (creating || inFlightRef.current) return;
    setCreating(true);
    inFlightRef.current = true;
    try {
      // Pass selected shipping address ID and priority to pre-order creation
      // Priority is URGENT only if Lenovo Top Choice products exist and express shipping is selected
      const orderData = {
        ...(selectedShippingAddress?.id ? { shipping_address_id: selectedShippingAddress.id } : {}),
        ...(hasLenovoTopChoice && shippingPriority === 'express' ? { priority: 'URGENT' } : {})
      };
      const order = await createOrderFromCart(orderData);
      setCart((prev) =>
        prev
          ? {
              ...prev,
              items: [],
              item_count: 0,
              subtotal: 0,
              discount_total: 0,
              tax_total: 0,
              shipping_cost: 0,
              grand_total: 0,
            }
          : prev
      );
      window.dispatchEvent(new CustomEvent("cart:updated", { detail: { action: "checkout" } }));
      showToast(`Pre-order created: ${order?.order_number || order?.id}`, "success");
      // Close cart window after successful pre-order creation
      if (onClose) {
        onClose();
      }
    } catch (e) {
      console.error(e);
      showToast("Failed to create pre-order. Please try again.", "error");
    } finally {
      setTimeout(() => {
        setCreating(false);
        inFlightRef.current = false;
      }, 200);
    }
  };

  const handleProceedToPreorder = () => {
    // For non-logged-in users, redirect to proceed to preorder page
    navigate("/proceed-to-preorder");
  };

  return (
    <CartContainerWrapper className={scrolled ? "scrolled" : ""}>
      <ClearCartWrapper>
        <Button variant="outlined" onClick={handleClear} disabled={!items.length || creating}>
          Clear Cart
        </Button>
      </ClearCartWrapper>

      <CartItemsWrapper>
        {(isLoadingCart || isLoadingProducts) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isLoadingCart ? 'Loading cart...' : 'Loading products...'}
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Your cart is empty
            </Typography>
          </Box>
        ) : (
          items.map((item) => {
            const qty = Number(item.quantity || 1);
            const stock = getStockQty(item);
            const finiteStock = Number.isFinite(stock);

          return (
            <ProductCard key={item.id ? `cart-item-${item.id}` : `guest-item-${item.product_id}`}>
              <ProductImage
                sx={{
                  backgroundImage: `url(${getProductImage(item)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
              <ProductCardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: 14, lineHeight: 1.2 }}>
                  {productDetails[item.product_id]?.description || item.product_name || "Product"}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 12, color: '#666' }}>
                  {productDetails[item.product_id]?.category || item.brand || "—"}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 12, color: '#666' }}>
                  {item.product_sku || "—"}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 'bold', color: 'green' }}>
                  {(() => {
                    const product = productDetails[item.product_id];
                    // Check if product is ON_DEMAND
                    // Priority 1: Check productDetails pricing_mode (most reliable)
                    const productPricingMode = product?.pricing_mode;
                    // Priority 2: Check if unit_price is 0/null/undefined (fallback if productDetails not loaded yet)
                    const unitPriceNum = Number(item.unit_price || 0);
                    const isOnDemand = productPricingMode === 'ON_DEMAND' || 
                                       (unitPriceNum === 0 && productPricingMode !== 'FIXED');
                    return isOnDemand ? "Price on Demand" : numberFmt(item.unit_price);
                  })()}
                </Typography>
                <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                  <Typography variant="body2" component="span" sx={{ fontSize: 12 }}>
                    Qty:
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => changeQtyBy(item, -1)}
                    disabled={qty <= 1 || creating}
                    aria-label="decrease quantity"
                    sx={{ padding: '2px', minWidth: '24px', height: '24px' }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <TextField
                    size="small"
                    value={qty}
                    onChange={(e) => handleQty(item, e.target.value)}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: { width: 40, textAlign: "center", fontSize: 12, padding: '4px' },
                    }}
                    disabled={creating}
                    sx={{ '& .MuiInputBase-root': { height: '24px' } }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => changeQtyBy(item, +1)}
                    disabled={creating || (finiteStock && qty >= stock)}
                    aria-label="increase quantity"
                    sx={{ padding: '2px', minWidth: '24px', height: '24px' }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  {finiteStock && (
                    <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7, marginLeft: 4 }}>
                      / {stock}
                    </Typography>
                  )}
                </div>
              </ProductCardContent>
              <ProductCardActions>
                <Button 
                  variant="outlined" 
                  onClick={() => handleRemove(item)} 
                  disabled={creating}
                  size="small"
                  sx={{ fontSize: 10, padding: '4px 8px', minWidth: '60px' }}
                >
                  Delete
                </Button>
              </ProductCardActions>
            </ProductCard>
          );
          })
        )}
      </CartItemsWrapper>

      <CartSummaryWrapper>
        <CartSummaryContent>
          {auth && (
            <SummaryLeftSection>
              <ToSectionWrapper>
                <ToSectionHeader>
                  <Typography variant="subtitle2">Shipping To:</Typography>
                  <Button 
                    variant="outlined" 
                    onClick={handleOpenAddressModal}
                    disabled={!allShippingAddresses || allShippingAddresses.length === 0}
                  >
                    Change
                  </Button>
                </ToSectionHeader>
                <Divider sx={{ mb: 1 }} />
                <ToSectionContent>
                  <Typography variant="body2">Name: {panelName}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Address: {isAddressExpanded ? formatFullAddress(selectedShippingAddress) : panelAddress}
                    </Typography>
                    {selectedShippingAddress && panelAddress !== "—" && (
                      <IconButton
                        size="small"
                        onClick={() => setIsAddressExpanded(!isAddressExpanded)}
                        sx={{ 
                          padding: '2px',
                          marginTop: '-2px',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        {isAddressExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="body2">Mobile No.: {panelMobile}</Typography>
                </ToSectionContent>
              </ToSectionWrapper>
              
              {/* Shipping Priority Option - Show only for Lenovo Top Choice products */}
              {/* LCR products: Express shipping ($215) is automatically added by backend */}
              {hasLenovoTopChoice && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                    Shipping Priority
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                        <Checkbox
                          checked={shippingPriority === 'express'}
                          onChange={async (e) => {
                            const newPriority = e.target.checked ? 'express' : 'normal';
                            const expressPriority = hasLenovoTopChoice && newPriority === 'express';
                            
                            // Update state
                            setShippingPriority(newPriority);
                            
                            // Immediately trigger shipping recalculation with new priority
                            // Pass the express priority directly to avoid state update delay
                            calculateShipping(expressPriority);
                          }}
                          size="small"
                          disabled={isCalculatingShipping}
                        />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            Upgrade to Express Shipping (+$215)
                          </Typography>
                        }
                        sx={{ margin: 0 }}
                      />
                      {isCalculatingShipping && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 4.5, mt: 0.5 }}>
                      Express orders are processed and delivered faster than standard shipping.
                    </Typography>
                  </Box>
                </Box>
              )}
            </SummaryLeftSection>
          )}

          <SummaryRightSection>
            <SummaryRightInner>
              {(isLoadingCart || isLoadingProducts || isCalculatingShipping || (auth && !shippingData && cart?.items?.length > 0)) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {isLoadingCart ? 'Loading cart...' : isLoadingProducts ? 'Loading products...' : isCalculatingShipping ? 'Calculating shipping...' : 'Loading billing details...'}
                  </Typography>
                </Box>
              ) : (
                <>
              <SummaryRightTop>
                <Typography>Subtotal: {totals.hasOnDemandItems ? "To be calculated" : numberFmt(totals.subtotal)}</Typography>
                
                {auth && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isCalculatingShipping && (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      )}
                      {shippingData?.breakdown?.length > 0 && (
                        <Tooltip
                          title={
                            <Box sx={{ 
                              maxWidth: 400, 
                              p: 2, 
                              backgroundColor: 'white', 
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              border: '1px solid #e0e0e0'
                            }}>
                              {shippingData.has_manual_quotes && shippingData.total_shipping_cost === 0 ? (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                                  Shipping will be calculated once order is confirmed
                                </Typography>
                              ) : (
                                <>
                                  {/* Total Items Header - Show once at top */}
                                  <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    fontWeight: 'bold', 
                                    color: '#333',
                                    borderBottom: '1px solid #e0e0e0',
                                    pb: 1
                                  }}>
                                    Total Items: {shippingData.breakdown.reduce((sum, item) => sum + (item.total_quantity || item.quantity || 0), 0)}
                                  </Typography>
                                  
                                  {shippingData.breakdown.map((item, index) => (
                                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < shippingData.breakdown.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                      {/* Items List */}
                                      <Typography variant="body2" sx={{ 
                                        mb: 1, 
                                        fontSize: '0.9rem', 
                                        lineHeight: 1.4, 
                                        whiteSpace: 'pre-line',
                                        color: '#555'
                                      }}>
                                        {item.items?.map((itemDetail, idx) => {
                                          // Get product name from productDetails if available
                                          const product = productDetails[itemDetail.product_id];
                                          const productName = product?.title || product?.name || itemDetail.description || itemDetail.name;
                                          return `${idx + 1}. ${productName} (${itemDetail.quantity})`;
                                        }).join('\n') || `${item.description || item.category} (${item.quantity})`}
                                      </Typography>
                                      
                                      {/* Shipping Cost */}
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold', 
                                        color: item.message && item.message.includes('To be calculated') ? '#ff9800' : '#333',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}>
                                        <span>Shipping:</span>
                                        <span>
                                          {item.message && item.message.includes('To be calculated') 
                                            ? 'To be calculated' 
                                            : (item.message || numberFmt(item.cost))
                                          }
                                        </span>
                                      </Typography>
                                      
                                      {/* Removed duplicate asterisk message - only show at bottom */}
                                    </Box>
                                  ))}
                                  
                                  {/* Show overall asterisk message if any manual quotes */}
                                  {shippingData.has_manual_quotes && (
                                    <Typography variant="caption" sx={{ 
                                      color: '#ff9800', 
                                      fontSize: '0.75rem',
                                      fontStyle: 'italic',
                                      display: 'block',
                                      mt: 1,
                                      pt: 1,
                                      borderTop: '1px solid #f0f0f0'
                                    }}>
                                      * Final shipping amount will be calculated post order confirmation
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <span>
                        Shipping: {
                          totals.shipping === null
                            ? "To be calculated"
                            : numberFmt(totals.shipping)
                        }
                      </span>
                    </Box>
                    {shippingData?.has_manual_quotes && (
                      <Typography variant="caption" sx={{ 
                        color: '#ffa726', 
                        fontSize: '0.75rem',
                        mt: 0.5,
                        fontStyle: 'italic'
                      }}>
                        * Final shipping amount will be calculated post order confirmation
                      </Typography>
                    )}
                    {totals.showShippingDisclaimer && !shippingData?.has_manual_quotes && (
                      <Typography variant="caption" sx={{ 
                        color: '#ffa726', 
                        fontSize: '0.75rem',
                        mt: 0.5,
                        fontStyle: 'italic'
                      }}>
                        The final shipping amount may vary based on actual shipping costs at the time of dispatch.
                      </Typography>
                    )}
                  </Box>
                )}
                {auth && (
                  <Typography>
                    Discount:{' '}
                    {totals.hasOnDemandItems
                      ? '$0.00'
                      : totals.discount > 0
                        ? `-$${Number(totals.discount).toFixed(2)}`
                        : '$0.00'}
                  </Typography>
                )}
                {auth && (
                  <Typography>VAT: {totals.tax === null ? "To be calculated" : numberFmt(totals.tax)}</Typography>
                )}
                <Divider sx={{ my: 1 }} />
              </SummaryRightTop>
              <SummaryRightSpacer />
              <SummaryRightBottom>
                <Box>
                  <Typography variant="h6">
                    Total: {totals.hasOnDemandItems || totals.total === null ? "To be calculated" : numberFmt(totals.total)}
                  </Typography>
                  {totals.showTotalDisclaimer && (
                    <Typography variant="caption" sx={{ 
                      color: '#ffa726', 
                      fontSize: '0.75rem',
                      mt: 0.5,
                      fontStyle: 'italic',
                      display: 'block'
                    }}>
                      The final total amount may vary based on actual shipping costs at the time of dispatch.
                    </Typography>
                  )}
                </Box>
              </SummaryRightBottom>
                </>
              )}
            </SummaryRightInner>
          </SummaryRightSection>
        </CartSummaryContent>
      </CartSummaryWrapper>

      <CartActionsWrapper>
        <Button variant="outlined" onClick={onClose} disabled={creating}>
          Back
        </Button>
        {auth ? (
        <Button
          variant="contained"
          color="primary"
            disabled={!items.length || creating}
          onClick={handleCreatePreorder}
        >
          {creating ? "Creating…" : "Create Pre-order"}
        </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            disabled={!items.length || creating}
            onClick={handleProceedToPreorder}
          >
            PROCEED TO PRE-ORDER
          </Button>
        )}
      </CartActionsWrapper>
      
      {/* Address Selection Modal */}
      <Modal
        open={addressModalOpen}
        onClose={handleCloseAddressModal}
        aria-labelledby="address-selection-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          maxWidth: 600,
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          overflow: 'auto'
        }}>
          <Typography variant="h6" id="address-selection-modal" sx={{ mb: 2, fontWeight: 'bold' }}>
            Select Shipping Address
          </Typography>
          
          {allShippingAddresses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No shipping addresses found. Please add an address in your profile.
            </Typography>
          ) : (
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedAddressId || ''}
                onChange={(e) => setSelectedAddressId(Number(e.target.value))}
              >
                {allShippingAddresses.map((address) => (
                  <Box
                    key={address.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: selectedAddressId === address.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: selectedAddressId === address.id ? '#f3f8ff' : 'white',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedAddressId === address.id ? '#f3f8ff' : '#f5f5f5'
                      }
                    }}
                    onClick={() => setSelectedAddressId(address.id)}
                  >
                    <FormControlLabel
                      value={address.id}
                      control={<Radio />}
                      label={
                        <Box sx={{ width: '100%', ml: 1 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            mb: 0.5, 
                            width: '100%',
                            gap: 1
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {address.recipient_name || 'N/A'}
                              {address.is_default && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                                  (Default)
                                </Typography>
                              )}
                            </Typography>
                            {!address.is_default && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await updateAddress(address.id, { is_default: true });
                                    showToast('Address set as default successfully', 'success');
                                    // Reload addresses and update cart panel
                                    const prof = await getProfile();
                                    const p = prof?.profile || prof;
                                    const addresses = Array.isArray(p?.addresses) ? p.addresses : [];
                                    const shippingAddresses = addresses.filter((a) => a.is_shipping);
                                    setAllShippingAddresses(shippingAddresses);
                                    
                                    // Update selected address if it became default
                                    const newDefault = shippingAddresses.find((a) => a.is_default) || shippingAddresses[0];
                                    if (newDefault && newDefault.id === address.id) {
                                      setSelectedShippingAddress(newDefault);
                                      setSelectedAddressId(newDefault.id);
                                      setPanelName(newDefault?.recipient_name || "—");
                                      setPanelMobile(newDefault?.phone_number || "—");
                                      setPanelAddress(formatAddressLine(newDefault));
                                    }
                                  } catch (error) {
                                    showToast('Failed to set as default', 'error');
                                  }
                                }}
                                sx={{ 
                                  fontSize: '0.7rem', 
                                  minWidth: '100px',
                                  padding: '2px 8px',
                                  textTransform: 'none',
                                  flexShrink: 0
                                }}
                              >
                                Set as Default
                              </Button>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {address.street_address}
                          </Typography>
                          {address.suburb_locality && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {address.suburb_locality}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {[address.city_town, address.state_province_region, address.postal_zip_code]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                          {address.country && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {address.country}
                            </Typography>
                          )}
                          {address.phone_number && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                              Phone: {address.phone_number}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={handleCloseAddressModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAddress}
              disabled={!selectedAddressId || allShippingAddresses.length === 0}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </CartContainerWrapper>
  );
}